import { useState } from 'react'
import dynamic from 'next/dynamic'
import get from 'lodash/get'
import styled, { css } from 'styled-components'

import { breakpoints } from 'bl-common/src/constants/breakpoints'
import { gridOffsetFrom, gridOffsetTo } from 'bl-common/src/constants/sizes'
import { Button } from 'bl-common/src/elements/Button/Button'
import { Type } from 'bl-common/src/elements/Typography/Typography'
import { Appear } from 'bl-common/src/units/Appear'
import { Carousel } from 'bl-common/src/units/Carousel/Carousel'
import { LightBoxItem } from 'bl-common/src/units/LightBox/LightBoxItem'
import { Section } from 'bl-common/src/units/Section/Section'
import { SmartImage } from 'bl-common/src/units/SmartImage/SmartImage'
import { between } from 'bl-common/src/utils/between'
import { media, mediaMax } from 'bl-common/src/utils/media'

const Image = styled.div<{ url: string; offsetLeft?: boolean }>`
  background-size: cover;
  position: relative;
  background-image: url(${({ url }) => url});
  background-position: center;
  height: 60vh;

  ${({ offsetLeft }) =>
    offsetLeft &&
    css`
      margin-left: ${gridOffsetFrom}px;
      ${media.md(css`
        margin-left: ${between(gridOffsetFrom, gridOffsetTo)};
      `)}
    `}

  ${media.md(css`
    max-height: 1100px;
    height: 85vh;
  `)}
`

const FlexContainer = styled.div`
  display: flex;
  ${mediaMax.md(css`
    flex-wrap: wrap;
  `)};
  .Appear {
    width: 100%;
  }
`

const GalleryItem = styled.div`
  display: flex;
  flex: 0 1 100%;
  flex-wrap: wrap;
  position: relative;
  justify-content: space-between;
  align-items: center;
  &:first-child {
    margin-bottom: 40px;
    ${media.md(css`
      margin-bottom: ${({ theme }) => theme.spacing[8]};
    `)}
  }
  ${media.md(css`
    flex: 0 1 50%;
    &:first-child {
      margin-right: ${between(63, 106, { fromWidth: breakpoints.md })};
      margin-bottom: 0;
    }
  `)}
`

const CinemaButton = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`

const Container = styled.div`
  position: relative;
`

const Cinema = dynamic(() => import('bl-common/src/units/Cinema/Cinema'), {
  ssr: false,
})

let galleryId = 0

export const PlayIcon = () => (
  <svg
    width={between(70, 114)}
    height={between(70, 114)}
    viewBox="0 0 114 114"
    fill="none"
  >
    <circle opacity="0.8" cx="57" cy="57" r="57" fill="black" />
    <path
      opacity="0.8"
      d="M44.375 79.4934C43.9428 79.4934 43.5105 79.3512 43.1258 79.065C42.4212 78.5438 42 77.6397 42 76.6483V36.8439C42 35.8526 42.4212 34.9485 43.1258 34.4272C43.8319 33.906 44.6964 33.8586 45.4358 34.3021L78.6858 54.2043C79.4965 54.6877 80 55.6619 80 56.7461C80 57.8303 79.4965 58.8046 78.6874 59.2898L45.4374 79.192C45.1002 79.3929 44.736 79.4934 44.375 79.4934ZM44.3766 35.8943C44.1818 35.8943 44.0314 35.9853 43.957 36.0384C43.8462 36.1218 43.5833 36.3682 43.5833 36.8439V76.6483C43.5833 77.1241 43.8462 77.3705 43.9586 77.4539C44.0726 77.5354 44.3734 77.7117 44.7297 77.4956L77.9797 57.5934C78.252 57.4304 78.4167 57.112 78.4167 56.7461C78.4167 56.3803 78.252 56.0619 77.9797 55.8989L44.7297 35.9967C44.603 35.9208 44.4843 35.8943 44.3766 35.8943Z"
      fill="white"
    />
  </svg>
)

const GalleryMedia = ({ image, caption, videoId, openCinema }) => {
  return (
    <Appear delay={300}>
      <Container>
        <LightBoxItem image={image}>
          <SmartImage image={image}>
            {imageUrl => <Image url={imageUrl} />}
          </SmartImage>
        </LightBoxItem>
        {caption && (
          <Type preset="labelSmall" weight="normal" top={{ xs: 0.5, lg: 1 }}>
            {caption}
          </Type>
        )}
        {!!videoId && (
          <CinemaButton>
            <Button onClick={() => openCinema(videoId)} noHover transparent>
              <PlayIcon />
            </Button>
          </CinemaButton>
        )}
      </Container>
    </Appear>
  )
}

const SingleGallery = ({
  image: {
    fields: { image, caption, videoId },
  },
  openCinema,
}) => {
  return (
    <GalleryMedia
      image={image}
      caption={caption}
      videoId={videoId}
      openCinema={openCinema}
    />
  )
}

const DoubleGallery = ({ images, openCinema }) => {
  const [image1, image2] = images
  return (
    <FlexContainer>
      <GalleryItem>
        <GalleryMedia
          image={get(image1, 'fields.image')}
          caption={get(image1, 'fields.caption')}
          videoId={get(image1, 'fields.videoId')}
          openCinema={openCinema}
        />
      </GalleryItem>
      <GalleryItem>
        <GalleryMedia
          image={get(image2, 'fields.image')}
          caption={get(image2, 'fields.caption')}
          videoId={get(image2, 'fields.videoId')}
          openCinema={openCinema}
        />
      </GalleryItem>
    </FlexContainer>
  )
}

const CarouselGallery = ({ images, openCinema }) => {
  return (
    <Carousel
      scrollKey={`gallery${galleryId++}`}
      minItemWidthFrom={200}
      minItemWidthTo={457}
    >
      {images.map(({ fields: { image, caption, videoId } }, i) => (
        <GalleryMedia
          key={i}
          image={image}
          caption={caption}
          videoId={videoId}
          openCinema={openCinema}
        />
      ))}
    </Carousel>
  )
}

const StoryGallery = ({ images }) => {
  let ContentComponent
  let topConfig
  let bottomConfig

  const [video, setVideo] = useState(null)

  const openCinema = videoId => setVideo(videoId)

  switch (images.length) {
    case 0:
      return null
    case 1:
      topConfig = { xs: 2.5, md: 6 }
      bottomConfig = { xs: 2.5, md: 6 }
      ContentComponent = (
        <SingleGallery image={images[0]} openCinema={openCinema} />
      )
      break
    case 2:
      topConfig = { xs: 2.5, md: 6 }
      bottomConfig = { xs: 2.5, md: 6 }
      ContentComponent = (
        <DoubleGallery images={images} openCinema={openCinema} />
      )
      break
    default:
      topConfig = { xs: 2.5, md: 6 }
      bottomConfig = { xs: 2.5, md: 6 }
      ContentComponent = (
        <CarouselGallery images={images} openCinema={openCinema} />
      )
  }

  return (
    <Section top={topConfig} bottom={bottomConfig}>
      {ContentComponent}
      {!!video && (
        <Cinema show={!!video} onHide={() => setVideo(false)} videoId={video} />
      )}
    </Section>
  )
}

export default StoryGallery
